import React from 'react';
import InputBase, { InputBaseProps, innerTextInputStyles, inputContainerStyle } from '../InputBase';
import { Loader } from '../../Loader';
import { StyleProp, TextInput, View, ViewStyle } from 'react-native';
import OutsidePressHandler from 'react-native-outside-press';
import { getSpacing } from '../../../../utils/spacing';
import Stack from '../../Stack';
import Chip from '../../Chip';
import Text from '../../Text';
import { useIsDisabledByParent } from '../../IsDisabledProvider';
import { Paper } from '../../Paper';
import { useIsMobileLayout } from '../../../../hooks/useIsMobileLayout';
import { FixedContentContainer } from '../../FixedPositionPortalContainer';
import PressableDynamicOpacity from '../../PressableDynamicOpacity';
import { colors } from '../../../../utils/theme';
import Divider from '../../Divider';
import SelectOption from './SelectOption';
import { PRFlatList } from '../../../shared/Scroll';
import { useSuggestionPosition } from './useSuggestionPosition';
import { useAutoCompleteInputEventOrchestration } from './useAutoCompleteInputEventOrchestration';

export type AutocompleteInputProps<T = any> = Pick<
    InputBaseProps,
    'onBlur' | 'onFocus' | 'isDisabled' | 'title' | 'error' | 'forceFullWidth'
> & {
    filteredItems: T[];
    keyExtractor: (item: T) => string;
    displayExtractor: (item: T) => string;
    subDisplayExtractor?: (item: T) => string;
    onChange: (item: T | null) => void;
    isLoadingItems?: boolean;
    containerStyle?: StyleProp<ViewStyle>;
    value: T | null;
    textValue: string;
    textOnChange: (value: string) => void;
    testID?: string;
    getOptionTestID?: (item: T) => string;
    getChipTestID?: (item: T) => string;
    emptyText?: string;
};

function AutocompleteInput<TValue>({
    filteredItems,
    keyExtractor,
    isLoadingItems,
    containerStyle,
    onChange,
    value,
    displayExtractor,
    textValue,
    textOnChange,
    testID,
    getOptionTestID,
    emptyText,
    subDisplayExtractor,
    getChipTestID,
    ...inputProps
}: AutocompleteInputProps<TValue>) {
    const isMobile = useIsMobileLayout();
    const textInputRef = React.useRef<TextInput | null>(null);
    const isDisabledByParent = useIsDisabledByParent(inputProps.isDisabled);
    const {
        onInputFocus,
        onInputBlur,
        onPressOutsideOfInput,
        onPressOutsideOfSuggestions,
        onSuggestionFocus,
        onSuggestionTouchStart,
        onFocusInputFromValueDeletion,
        onInputAreaPress,
        onSuggestionSelected,
        isAutoCompleteFocused,
    } = useAutoCompleteInputEventOrchestration({
        onBlurFromParent: inputProps.onBlur as (() => void) | undefined,
        onFocusFromParent: inputProps.onFocus,
        textInput: textInputRef.current || null,
    });

    const shouldShowAutocomplete =
        !!(filteredItems.length > 0 || isLoadingItems || emptyText) &&
        isAutoCompleteFocused &&
        !inputProps.isDisabled &&
        !value;

    const suggestionsStyling = {
        backgroundColor: 'white',
        position: 'absolute',
        borderWidth: 1,
        borderRadius: 5,
        borderColor: colors.Dividers,
    } as const;

    const { inputRef, onInputLayout, suggestionPositionStyling, suggestionAboveOrBelow } = useSuggestionPosition({
        shouldShowSuggestions: shouldShowAutocomplete,
    });

    const renderSuggestionsContent = () => {
        if (!shouldShowAutocomplete) return null;

        const renderInContainer = (children: React.ReactNode) => (
            <View
                style={{
                    width: '100%',
                    alignItems: 'center',
                    padding: getSpacing(2.5),
                }}
            >
                {children}
            </View>
        );

        return (
            <FixedContentContainer>
                <Paper style={[suggestionsStyling, suggestionPositionStyling]}>
                    {isLoadingItems ? (
                        renderInContainer(<Loader size="sm" />)
                    ) : filteredItems?.length ? (
                        <OutsidePressHandler
                            onOutsidePress={onPressOutsideOfSuggestions}
                            style={{ flex: 1, maxHeight: '100%' }}
                        >
                            <PRFlatList
                                contentContainerStyle={{ flexGrow: 1 }}
                                keyExtractor={keyExtractor}
                                renderItem={({ item, index }) => (
                                    <PressableDynamicOpacity
                                        key={keyExtractor(item)}
                                        testID={getOptionTestID?.(item) || `autocomplete-item-${keyExtractor(item)}`}
                                        onFocus={onSuggestionFocus}
                                        onTouchStart={onSuggestionTouchStart}
                                        onPress={() => {
                                            onSuggestionSelected();
                                            onChange(item);
                                        }}
                                        style={{
                                            width: '100%',
                                            paddingTop: 2,
                                        }}
                                    >
                                        <Stack vertical fullWidth space={1}>
                                            <SelectOption
                                                isLast={index === filteredItems.length - 1}
                                                primaryDisplay={displayExtractor(item)}
                                                subDisplay={subDisplayExtractor?.(item)}
                                            />
                                            {index < filteredItems.length - 1 && <Divider horizontal />}
                                        </Stack>
                                    </PressableDynamicOpacity>
                                )}
                                data={filteredItems}
                                keyboardShouldPersistTaps="handled"
                            />
                        </OutsidePressHandler>
                    ) : (
                        renderInContainer(
                            <Text variant="bold-body-1" color="Gray-Text">
                                {emptyText}
                            </Text>,
                        )
                    )}
                </Paper>
            </FixedContentContainer>
        );
    };

    return (
        <View
            style={[
                { zIndex: 1 },
                containerStyle,
                isMobile || inputProps.forceFullWidth ? inputContainerStyle.mobile : inputContainerStyle.desktop,
            ]}
            ref={inputRef}
            onLayout={onInputLayout}
            testID="autocomplete-input"
        >
            {suggestionAboveOrBelow === 'above' ? renderSuggestionsContent() : null}
            <OutsidePressHandler
                onOutsidePress={() => {
                    onPressOutsideOfInput();
                }}
            >
                <View
                    style={[
                        isMobile || inputProps.forceFullWidth
                            ? inputContainerStyle.mobile
                            : inputContainerStyle.desktop,
                    ]}
                    ref={inputRef}
                    onLayout={onInputLayout}
                >
                    <InputBase
                        {...inputProps}
                        isDisabled={inputProps.isDisabled}
                        onBlur={onInputBlur}
                        onFocus={onInputFocus}
                        onInputPress={onInputAreaPress}
                        value={textValue || (value ? displayExtractor(value) : null)}
                        renderInputField={({ onFocus, onBlur }) => {
                            const getChipText = () => {
                                if (!value) return '';

                                if (!subDisplayExtractor?.(value)) {
                                    return displayExtractor(value);
                                }

                                const subDisplayText = subDisplayExtractor(value);
                                const combinedLength = displayExtractor(value).length + subDisplayText.length;
                                if (combinedLength < 50) {
                                    return `${displayExtractor(value)} - ${subDisplayText}`;
                                } else {
                                    return `${displayExtractor(value)}\n${subDisplayText}`;
                                }
                            };

                            return (
                                <Stack horizontal fullWidth align="flex-end" style={{ height: value ? undefined : 24 }}>
                                    <Stack horizontal flex={1}>
                                        {value ? (
                                            <Stack horizontal justify="flex-start" fullWidth testID="chip-container">
                                                <Chip
                                                    title={getChipText()}
                                                    highlight
                                                    isDisabled={isDisabledByParent}
                                                    onDelete={() => {
                                                        onChange(null);
                                                        textOnChange('');
                                                        onFocusInputFromValueDeletion();
                                                    }}
                                                    testID={getChipTestID?.(value)}
                                                />
                                            </Stack>
                                        ) : null}
                                        <TextInput
                                            testID={testID}
                                            ref={textInputRef}
                                            style={[innerTextInputStyles, value ? { width: 0 } : {}]}
                                            caretHidden={false}
                                            onFocus={(_e) => {
                                                onFocus();
                                            }}
                                            onBlur={onBlur}
                                            value={textValue}
                                            onChange={(e) => {
                                                if (!value) {
                                                    textOnChange(e.nativeEvent.text);
                                                }
                                            }}
                                        />
                                    </Stack>
                                    {!value && isLoadingItems ? <Loader size="sm" /> : null}
                                </Stack>
                            );
                        }}
                    />
                </View>
            </OutsidePressHandler>
            {suggestionAboveOrBelow === 'below' ? renderSuggestionsContent() : null}
        </View>
    );
}

export default AutocompleteInput;
